import { useState } from 'react';
import { cn } from '../../styles/lib/utils';
import { IconButton } from '../Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../DropdownMenu';
import { Icon, type IconName } from '../Icon';
import { Text } from '../Text';

export type AdvancedFilterActionMenuAction = {
  label: string;
  onClick: () => void;
  icon?: IconName;
  variant?: 'default' | 'destructive';
};

export type AdvancedFilterActionMenuProps = {
  actions: AdvancedFilterActionMenuAction[];
  onOpenChange?: (open: boolean) => void;
  depth?: number;
  'aria-label'?: string;
};

function AdvancedFilterActionMenu({
  actions,
  onOpenChange,
  depth = 0,
  'aria-label': ariaLabel = 'Filter actions',
}: AdvancedFilterActionMenuProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <IconButton
          icon="dotsVertical"
          variant={open ? 'selected' : 'hollow'}
          size="icon"
          aria-label={ariaLabel}
          className={cn(
            'h-6 px-0! py-0.5! rounded-sm',
            depth === 0 ? 'hover:bg-grey-20' : depth % 2 === 0 ? 'hover:bg-grey-30' : 'hover:bg-grey-20',
          )}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="right" className="w-auto min-w-[160px]">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            variant={action.variant}
            onClick={() => {
              action.onClick();
            }}
          >
            {action.icon && <Icon icon={action.icon} size={14} />}
            <Text className="text-xs">{action.label}</Text>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { AdvancedFilterActionMenu };
